<template>
  <div
    :class="['hero', { 'hero--overlay': overlay === 'true' }]"
    data-ui="hero"
    :aria-label="image.alt"
  >
    <div class="hero__inner">
      <div class="hero__bg">
        <img
          :src="backgroundImage"
          :alt="image.alt"
          class="hero__bg-img hero__bg-img--lg"
        >
      </div>
      <div
        :class="['hero__upper', { 'hero__upper--overlay': overlay === 'true' }]"
        data-ui="hero-upper"
      >
        <img
          :src="backgroundImage"
          :alt="image.alt"
          class="hero__bg-img hero__bg-img--sm"
        >
        <h1
          class="hero__heading"
          v-html="heading"
        />
      </div>
      <div class="hero__lower">
        <cdr-text
          class="hero__subheading"
        >
          {{ subheading }}
        </cdr-text>
        <cdr-button
          v-if="!!ctaText"
          :data-analytics-id="`header_${ctaText}`"
          :href="ctaHref"
          tag="a"
          class="hero__cta"
          modifier="secondary"
        >
          {{ ctaText }}
        </cdr-button>
      </div>
    </div>
  </div>
</template>

<script>
import { CdrButton, CdrText } from '@rei/cedar';

export default {
  name: 'MemberCollectionHero',
  components: {
    CdrButton,
    CdrText,
  },
  props: {
    overlay: {
      type: String,
      default: 'false',
    },
    ctaHref: {
      type: String,
      required: false,
      default: '',
    },
    ctaText: {
      type: String,
      required: false,
      default: '',
    },
    heading: {
      type: String,
      required: true,
    },
    screenWidth: {
      type: Number,
      required: true,
    },
    subheading: {
      type: String,
      required: true,
    },
    image: {
      type: Object,
      required: true,
    },
  },
  computed: {
    backgroundImage() {
      return `${this.image.src}?im=RegionOfInterestCrop=(2700,776),regionOfInterest=(3000,3000)`;
    },
  },
};
</script>

<style lang="scss">

.hero {
  position: relative;
  padding-bottom: $cdr-space-half-x;
  @include cdr-lg-mq-up {
    position: relative;
    min-height: 517px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding-bottom: $cdr-space-four-x;
    margin-bottom: $cdr-space-four-x;
  }

  &.hero--overlay {
    @include cdr-lg-mq-up {
      &::before {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background-color: rgba(10, 10, 10, 0.4);
      }
    }
  }
}

.hero__inner {
  position: relative;
  @include cdr-lg-mq-up {
    height: 517px;
  }
}

.hero__bg {
  display: none;
  background-color: black;
  @include cdr-lg-mq-up {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
}

.hero__bg-img {
  object-fit: cover;
  object-position: center;
  height: 100%;
  width: 100%;
  opacity: 0.8;
}

.hero__bg-img--sm {
  @include cdr-lg-mq-up {
    display: none;
  }
}

.hero__bg-img--lg {
  display: none;
  @include cdr-lg-mq-up {
    display: block;
    object-fit: cover;
    object-position: center;
    height: 100%;
    width: 100%;
    opacity: 0.8;
  }
}

.hero__upper,
.hero__lower {
  position: relative;
  @include cdr-md-mq-up {
    @include cdr-container;
  }
}

.hero__upper {
  position: relative;
  // at least 188px tall, but grow as screen width increases
  height: clamp(188px, 40vw, 330px);
  width: 100%;
  display: flex;
  align-items: flex-end;
  background-color: black;

  @include cdr-sm-mq-up {
    min-height: 330px;
  }
  @include cdr-md-mq-only {
    padding: 0;
  }
  @include cdr-lg-mq-up {
    background: none;
    height: initial;
  }
}

.hero__heading {
  @include cdr-text-heading-display-1000;
  margin: 0;
  position: absolute;
  color: $cdr-color-text-inverse;
  left: $cdr-space-one-x;
  bottom: $cdr-space-one-x;

  span {
    font-style: italic;
    display: block;
  }

  @include cdr-lg-mq-up {
    position: initial;
    @include cdr-text-heading-display-1300;
    span {
      display: inline;
    }
  }
}

.hero__subheading {
  @include cdr-text-heading-sans-300;
  margin: $cdr-space-one-x 0;
  color: $cdr-color-text-brand;
  @include cdr-lg-mq-up {
    @include cdr-text-heading-sans-400;
    color: $cdr-color-text-inverse;
    max-width: 640px;
    font-weight: 500;
    padding-bottom: 0;
  }
}

.hero__lower {
  @include cdr-md-mq-down {
    padding: $cdr-space-one-x;
  }
  @include cdr-md-mq-up {
    padding-top: $cdr-space-one-x;
    padding-bottom: $cdr-space-one-x;
  }
  @include cdr-lg-mq-up {
    padding-top: 0;
  }
}

.hero__cta {
  justify-content: center;
  @include cdr-lg-mq-up {
    min-width: 109px; // they asked for it
  }
}
</style>
